/**
 * BalanceCard — hero balance treatment for B1 home dashboard.
 * Dark navy gradient with mono tabular amounts and orange Pay Now CTA.
 * Autopay gated on featureStatus SSoT (stub-catalog), not fixture flag.
 * Atlas: B1.
 */
import Link from "next/link";
import { ArrowRight, CheckCircle, Clock } from "lucide-react";
import { formatCurrency, formatDate, formatMonthNameLong, featureStatus } from "@petci/shared-ui";
import type { BillCycle } from "@petci/fixtures";
import { useT, useLocaleTag } from "@/lib/locale";

interface BalanceCardProps {
  /** What is still owed on this card's subject. Resolver: `useBillState`. */
  amount: number;
  /** The cycle (SSoT: billing-clock) — supplies the due date and next-bill month. */
  cycle: BillCycle;
  /**
   * True once THIS SESSION'S payments cover the bill. Never derived from the
   * date: the retired calendar window flipped every persona to "Paid ✓" from
   * the 23rd with no transaction behind it.
   */
  isSettled: boolean;
  /** `YYYY-MM-DD` the settling payment was actually made. Null while unsettled. */
  settledOnIso?: string | null;
  accountNumber: string;
  /**
   * False for a persona with no real bill (e.g. CSR/admin whose
   * `currentBill.month === "N/A"`): suppresses the due label, trend chip and pay
   * CTA so the card never shows a fabricated "Due 22 …" against a $0 balance
   * (H0.7). Defaults true.
   */
  hasBill?: boolean;
}

export function BalanceCard({
  amount,
  cycle,
  isSettled,
  settledOnIso = null,
  accountNumber,
  hasBill = true,
}: BalanceCardProps) {
  const t = useT();
  const localeTag = useLocaleTag();
  const autopayStatus = featureStatus("autopay");
  const autopayLive = autopayStatus.kind === "live";
  // A settled bill reads $0 because it was PAID, not because of the calendar.
  // A persona with no bill always reads $0.
  const displayAmount = !hasBill || isSettled ? 0 : amount;

  // R-A (Phase 2 CHECK): the white-opacity steps on this gradient are held at
  // the WCAG AA floor. /50 measured 4.18:1 at 11px and /40 measured 3.21:1 —
  // both sub-AA on the worst-case gradient stop. /70 and /60 restore the floor;
  // the pre-existing /60 due-line at 5.34:1 is the proof that the opacity
  // hierarchy (and so the visual design language) survives at AA.
  return (
    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-navy to-brand-secondary p-8 text-white shadow-lg">
      <p className="overline text-white/70">{t("balance.currentBalance")}</p>
      <p className="mt-2 font-mono text-5xl font-semibold tabular-nums tracking-tight">
        {formatCurrency(displayAmount)}
      </p>
      {!hasBill ? (
        <p className="mt-1 text-sm text-white/60">{t("balance.noBalanceDue")}</p>
      ) : isSettled ? (
        <>
          {/* Paid state: the green success token measured ~3.25:1 on this
              gradient (R-A). White carries the AA floor and the green stays
              semantic via the icon — the W7 dark-surface pattern (go lighter
              on dark, never darker). */}
          <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-white">
            <CheckCircle size={14} className="text-brand-accent" aria-hidden />
            {/* The date the customer actually paid — not the due date. The two
                were wired together while the calendar decided paid-ness. */}
            {t("billing.paidOn", { date: formatDate(settledOnIso, localeTag) })}
          </p>
          {/* F-W9-1: the month comes from the machine-readable `YYYY-MM`
              formatted against the active tag, never an English table.
              "Late", not "early": under the rolling cycle the next bill is
              issued on the 23rd of the due month. */}
          <p className="mt-0.5 text-xs text-white/70">
            {t("billing.nextBillLate", {
              month: formatMonthNameLong(cycle.nextBillMonthYm, localeTag),
            })}
          </p>
        </>
      ) : (
        <p className="mt-1 text-sm text-white/60">
          {t("balance.dueDate", { date: formatDate(cycle.dueDateIso, localeTag) })}
        </p>
      )}

      {/* Balance card keeps amount / due / pay only — the usage card owns the
          cost-trend chip (H1, was a duplicate here). */}
      <div className="mt-1 flex items-center gap-3">
        <span className="text-xs text-white/60 font-mono">
          #{accountNumber}
        </span>
      </div>

      {hasBill && (
      <div className="mt-6 flex items-center gap-3">
        {isSettled ? (
          // Settled: the account is current. A payment stays reachable via a
          // secondary CTA — a primary "Pay" beside a $0.00 balance contradicts
          // itself, but the route must not disappear.
          <Link
            href="/pay/amount"
            className="inline-flex items-center gap-1.5 rounded-sm border border-white/30 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors duration-fast focus-visible:ring-2 focus-visible:ring-white"
          >
            {t("pay.makePayment")}
            <ArrowRight size={14} aria-hidden />
          </Link>
        ) : amount > 0 ? (
          <>
            <Link
              href="/pay/amount"
              className="inline-flex items-center gap-1.5 rounded-sm bg-brand-primary-hover px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-primary-dark transition-colors duration-fast focus-visible:ring-2 focus-visible:ring-white ring-1 ring-white/20"
            >
              {t("balance.payNow")}
              <ArrowRight size={14} aria-hidden />
            </Link>
            {autopayLive ? (
              <Link
                href="/account/autopay"
                className="inline-flex items-center gap-1.5 rounded-sm border border-white/30 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/10 transition-colors duration-fast"
              >
                {t("balance.setUpAutopay")}
              </Link>
            ) : (
              <Link
                href="/account/autopay"
                className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs text-white/60 hover:bg-white/15 transition-colors duration-fast"
              >
                <Clock size={12} aria-hidden />
                {t("autopay.comingSoon")}
              </Link>
            )}
          </>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-semantic-success/20 px-4 py-2 text-sm font-semibold text-white">
            <CheckCircle size={14} className="text-brand-accent" aria-hidden />
            {t("balance.paidInFull")}
          </span>
        )}
      </div>
      )}
    </div>
  );
}
